/**
 * Connect and disconnect Facebook Pages and Instagram business accounts.
 *
 * The flow the user sees: press Connect → Facebook's consent screen → back here
 * with a picker of everything that login could connect → choose → done. The
 * middle hop is a full-page navigation to Facebook and a redirect back through
 * the backend, so this component is mounted twice per connection and picks up
 * where it left off from the URL — see `useConnectedAccounts`.
 *
 * Platform metadata (name, colours) comes from `config/platforms.ts`; which
 * platforms are connectable at all is the backend's provider registry. A
 * platform listed here without an adapter is refused at initialize with
 * PLATFORM_UNSUPPORTED rather than failing at the provider.
 */

import { AlertCircle, CheckCircle2, ExternalLink, Loader2, Unplug } from 'lucide-react';

import type { SocialAccountSummary, SocialPlatform } from '@app/common';
import { useTranslation } from '../../context/LanguageContext';
import { usePageHeader } from '../../context/PageHeaderContext';
import { PLATFORM_CONFIGS } from '../../config/platforms';
import type { PlatformConfig, PlatformId } from '../../config/platforms';
import { Button } from '../../components/core/Button';
import { ConnectTargetDialog } from '../../components/dashboard/accounts/ConnectTargetDialog';
import { useConnectedAccounts } from '../../hooks/useConnectedAccounts';

/** The platforms with a backend adapter today. Adding one is a registry entry there. */
const CONNECTABLE: PlatformId[] = ['facebook', 'instagram'];

export function ConnectedAccountsPage() {
  const { t } = useTranslation();
  usePageHeader({ title: t('accounts.title'), subtitle: t('accounts.subtitle') });

  const {
    accounts,
    pending,
    dismissPending,
    brandId,
    loading,
    error,
    clearError,
    busyPlatform,
    connect,
    finalize,
    disconnect,
  } = useConnectedAccounts();

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  // Every connection is filed under a brand, so there is genuinely nothing to
  // connect to before one exists. Said plainly rather than by disabling the
  // buttons and leaving the user to guess why.
  if (!brandId) {
    return (
      <p className="py-20 text-center text-sm text-slate-500">{t('accounts.noBrand')}</p>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
          {/*
            The code matters more than the sentence. `error` is either a message
            from our own API or the code the callback redirected with
            (CONSENT_DECLINED, NO_TARGETS, PROVIDER_REJECTED…), and every one of
            them names a different fix. Showing only the friendly line — which is
            what this did — hides the single most useful fact on the screen.
          */}
          <span className="flex-1 text-sm text-rose-700">
            {t('accounts.error')}
            <span className="mt-0.5 block font-mono text-xs text-rose-500">{error}</span>
          </span>
          <button
            type="button"
            onClick={clearError}
            className="text-xs font-semibold text-rose-600 hover:underline"
          >
            {t('common.dismiss')}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {CONNECTABLE.map((id) => (
          <PlatformCard
            key={id}
            platform={PLATFORM_CONFIGS[id]}
            accounts={accounts.filter((account) => account.platform === (id as SocialPlatform))}
            busy={busyPlatform === id}
            onConnect={() => void connect(id as SocialPlatform)}
            onDisconnect={(accountId) => void disconnect(accountId)}
          />
        ))}
      </div>

      {pending && (
        <ConnectTargetDialog
          connection={pending}
          onConfirm={finalize}
          onDismiss={dismissPending}
        />
      )}
    </div>
  );
}

function PlatformCard({
  platform,
  accounts,
  busy,
  onConnect,
  onDisconnect,
}: Readonly<{
  platform: PlatformConfig;
  accounts: SocialAccountSummary[];
  busy: boolean;
  onConnect: () => void;
  onDisconnect: (accountId: string) => void;
}>) {
  const { t } = useTranslation();
  const isConnected = accounts.length > 0;

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className={`h-1.5 w-full ${platform.bgClass}`} />

      <div className="flex flex-1 flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${platform.bgClass} ${platform.textClass}`}
          >
            {platform.name.slice(0, 2)}
          </div>

          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
              isConnected ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                isConnected ? 'bg-emerald-500' : 'bg-slate-400'
              }`}
            />
            {isConnected ? t('accounts.connected') : t('accounts.notConnected')}
          </span>
        </div>

        <div>
          <p className="font-semibold text-slate-800">{platform.name}</p>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            {t(`accounts.desc.${platform.id}` as 'accounts.desc.facebook')}
          </p>
        </div>

        {accounts.length > 0 && (
          <ul className="space-y-1.5">
            {accounts.map((account) => (
              <li
                key={account.id}
                className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-1.5"
              >
                {account.status === 'active' ? (
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                ) : (
                  // A dead token is a first-class state, not an error: the whole
                  // calendar for this account has silently stopped, and the only
                  // fix is the user reconnecting.
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                )}
                <span className="min-w-0 flex-1 truncate text-xs text-slate-700">
                  {account.externalName ?? account.externalId}
                </span>
                {account.status !== 'active' && (
                  <span className="shrink-0 text-[10px] font-semibold text-amber-600">
                    {t('accounts.needsReauth')}
                  </span>
                )}
                <button
                  type="button"
                  aria-label={t('accounts.disconnect')}
                  onClick={() => onDisconnect(account.id)}
                  className="shrink-0 text-slate-400 transition-colors hover:text-rose-500"
                >
                  <Unplug className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <Button
          variant={isConnected ? 'outline' : 'default'}
          className="mt-auto w-full"
          disabled={busy}
          onClick={onConnect}
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ExternalLink className="h-4 w-4" />
          )}
          {t(isConnected ? 'accounts.connectAnother' : 'accounts.connect')}
        </Button>
      </div>
    </div>
  );
}
